<script setup lang="ts">
  import { IconCurrency, IconTrash } from '@gat-ship/icons'
  import {
    Button,
    FlagIcon,
    Modal,
    ModalContent,
    ModalHeader,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection
  } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import type { Currency } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import { useCurrenciesModals } from '../providers/currencies-modals'

  const currenciesModals = useCurrenciesModals()!

  const currency = ref<Currency | undefined>(undefined)
  const isOpen = ref(false)

  export interface ShowCurrencyDetailsDialogParams {
    currency: Currency
  }

  function showModal(props: ShowCurrencyDetailsDialogParams) {
    currency.value = props.currency
    isOpen.value = true
  }

  defineExpose({
    showModal
  })
</script>

<template>
  <modal v-model:open="isOpen" type="drawer" close-on-outside-click>
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconCurrency class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        <div class="flex items-center gap-2">
          <FlagIcon
            v-if="currency?.flagCode"
            :flag="currency.flagCode"
            variant="rectangle"
            class="h-5"
          />
          <span>{{ currency?.code }} - {{ currency?.name }}</span>
        </div>
      </ModalHeaderTitle>
    </ModalHeader>
    <ModalContent>
      <div class="flex flex-col gap-5">
        <ModalSection>
          <div class="grid grid-cols-2 gap-2 gap-y-6 text-sm">
            <div class="flex flex-col gap-1">
              <span class="text-muted-foreground text-xs">Symbol</span>
              <span class="font-medium">{{ currency?.symbol }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-muted-foreground text-xs">Decimal digits</span>
              <span class="font-medium">{{ currency?.decimalDigits }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-muted-foreground text-xs">Minimal amount</span>
              <span class="font-medium">{{ currency?.minimalAmount }}</span>
            </div>
          </div>
        </ModalSection>
        <ModalSection>
          <Button
            v-if="currency"
            variant="outline"
            size="sm"
            class="w-fit"
            :data-testid="testIds.currency.action.removeCurrency"
            @click="currenciesModals.showRemoveCurrency({ currency })"
          >
            <IconTrash class="size-4" />
            Remove Currency
          </Button>
        </ModalSection>
      </div>
    </ModalContent>
  </modal>
</template>
